import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { usePlan } from './PlanContext';
import { usePermissions } from './PermissionsContext';
import { PILLAR_DEFS } from '../config/pillarDefs';

const PILLAR_KEY = 'crm_active_pillar';

const PillarContext = createContext(null);

export function PillarProvider({ children }) {
    const { checkModule } = usePlan();
    const { hasPillarAccess } = usePermissions();
    const location = useLocation();

    // Filter pillars by module gating + RBAC access
    const visiblePillars = useMemo(() => {
        return PILLAR_DEFS.filter(p => {
            // Module gating (e.g. HR requires 'ats' module)
            if (p.moduleKey && !checkModule(p.moduleKey)) return false;
            // RBAC gating — hide pillars user has no role in
            if (!hasPillarAccess(p.id)) return false;
            return true;
        });
    }, [checkModule, hasPillarAccess]);

    const [activePillarId, setActivePillarId] = useState(() => {
        try {
            const stored = localStorage.getItem(PILLAR_KEY);
            if (stored && PILLAR_DEFS.some(p => p.id === stored)) return stored;
        } catch {}
        return 'marketing';
    });

    // Auto-detect pillar from current URL path
    useEffect(() => {
        const path = location.pathname;
        for (const pillar of visiblePillars) {
            if (pillar.pathPrefixes.some(prefix => path === prefix || path.startsWith(prefix + '/'))) {
                if (pillar.id !== activePillarId) {
                    setActivePillarId(pillar.id);
                }
                return;
            }
        }
        // If no pillar matches (e.g. /dashboard, /inbox, /settings), keep current pillar
    }, [location.pathname, visiblePillars]);

    // Persist active pillar
    useEffect(() => {
        try { localStorage.setItem(PILLAR_KEY, activePillarId); } catch {}
    }, [activePillarId]);

    // If active pillar is no longer visible (module disabled or RBAC removed), fall back
    useEffect(() => {
        if (visiblePillars.length > 0 && !visiblePillars.some(p => p.id === activePillarId)) {
            setActivePillarId(visiblePillars[0].id);
        }
    }, [visiblePillars, activePillarId]);

    const activePillar = useMemo(() => {
        return visiblePillars.find(p => p.id === activePillarId) || visiblePillars[0] || PILLAR_DEFS[0];
    }, [activePillarId, visiblePillars]);

    const setActivePillar = useCallback((pillarId) => {
        setActivePillarId(pillarId);
    }, []);

    const value = useMemo(() => ({
        activePillar,
        activePillarId,
        setActivePillar,
        visiblePillars,
    }), [activePillar, activePillarId, setActivePillar, visiblePillars]);

    return (
        <PillarContext.Provider value={value}>
            {children}
        </PillarContext.Provider>
    );
}

export function usePillar() {
    const ctx = useContext(PillarContext);
    if (!ctx) throw new Error('usePillar must be used within PillarProvider');
    return ctx;
}
